/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";

import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Fade,
    Grid,
    IconButton,
    Modal,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { AssetWithTransfer, Effect, ModalBase } from "../../utils/Interfaces";
import { delayedAction } from "../../utils/Functions";
import CloseIcon from "@mui/icons-material/Close";
import { acceptAssetTransfer } from "./assetTransferFunctions";
import { useAppDispatch } from "../../app/hooks";
import { useWindowDimensions } from "../../utils/Hooks";

export interface InitiateAssetTransferModalProps extends ModalBase {
    asset: AssetWithTransfer;
    onAssetTransferAccepted: Effect;
}

const AcceptAssetTransferModal = (props: InitiateAssetTransferModalProps) => {
    const { open, handleClose, asset, onAssetTransferAccepted } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const theme = useTheme();

    const { width } = useWindowDimensions();
    const mobile: boolean = width <= 600;

    const [loading, setLoading] = useState<boolean>(false);

    const [notes, setNotes] = useState<string>("");

    function clearAssetValues(): void {
        setNotes("");
        setLoading(false);
    }

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            clearAssetValues();
        });
    }

    if (!asset) {
        return <Box></Box>;
    }

    return (
        <Modal
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        width: mobile ? "95vw" : "60vw",
                    }}
                >
                    <Box>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h5" textAlign="center" sx={{ ml: 2, mb: 2, width: "100%" }}>
                                Accept Transfer for {asset.name}
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start" sx={{ mr: -2 }}>
                                <IconButton aria-label="delete" onClick={onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Grid container>
                            <Grid xs={12} item>
                                <TextField
                                    disabled={loading}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    margin="normal"
                                    id="notes"
                                    name="notes"
                                    value={notes}
                                    label="Receipt Notes (if applicable)"
                                    size="small"
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNotes(event.target.value)}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mb: 5 }} />

                        {loading && (
                            <Stack direction="row" justifyContent="center">
                                <CircularProgress size={16} sx={{ mt: 1.25, mb: 1.25 }} />
                            </Stack>
                        )}

                        {!loading && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                                <Tooltip title="Discard changes">
                                    <Button variant="contained" color="secondary" sx={{ mr: 1 }} onClick={onClose}>
                                        Cancel
                                    </Button>
                                </Tooltip>
                                <Tooltip title="Update material">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        sx={{ ml: 1 }}
                                        onClick={() => {
                                            setLoading(true);

                                            acceptAssetTransfer(
                                                asset.transfer.id,
                                                dispatch,
                                                () => {
                                                    onAssetTransferAccepted();
                                                    onClose();
                                                },
                                                () => {}
                                            );
                                        }}
                                    >
                                        Accept Transfer
                                    </Button>
                                </Tooltip>
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default AcceptAssetTransferModal;
